// ImageCropper/cropAspectRatio.js
//
// builder:4.16 — ImageCropper (isWheelPortrait) crop aspect-ratio decision.
//
// The <Cropper> `aspectRatio` prop and the crop-option buttons both resolve an
// aspect ratio through the same precedence. Extracted from a 500+-line component
// so the (pure) ratio math is unit-testable without mounting cropperjs / a real
// <canvas> — the actual crop/apply/cancel legs are genuinely browser+canvas and
// stay deferred to a live pass; this covers the ratio DECISION only.
//
// Precedence (preserved byte-for-byte from the inline JSX ternary):
//   lockedAspectRatio (truthy) > wallpaper (0.85) > wheel-portrait (1)
//   > crop-tool option (Custom=NaN, 1:1=1, 3:2=1.5, 5:4=1.25, 16:9=16/9, else 1)
//   > NaN (free-form when no crop tool active)

/**
 * Map a crop-option label to its aspect ratio. Unlisted / undefined falls back
 * to 1 (the inline JSX crop branch's final `: 1`).
 * @param {string} option
 * @returns {number} aspect ratio (NaN for free-form "Custom")
 */
export function optionToAspectRatio(option) {
  switch (option) {
    case "Custom":
      return NaN;
    case "1:1":
      return 1;
    case "3:2":
      return 3 / 2;
    case "5:4":
      return 5 / 4;
    case "16:9":
      return 16 / 9;
    default:
      return 1;
  }
}

/**
 * Resolve the <Cropper> aspectRatio prop from the current cropper mode.
 * @param {object} o
 * @param {number} [o.lockedAspectRatio]   a forced ratio (wins over everything)
 * @param {boolean} [o.isWallpaperCropper]  wallpaper mode -> 0.85
 * @param {boolean} [o.isWheelPortrait]     wheel portrait mode -> 1 (square/circular)
 * @param {string}  [o.activeTool]          the active tool ("crop" enables options)
 * @param {string}  [o.selectedOption]      the selected crop option label
 * @returns {number} aspect ratio (may be NaN for free-form)
 */
export function resolveCropperAspectRatio({
  lockedAspectRatio,
  isWallpaperCropper,
  isWheelPortrait,
  activeTool,
  selectedOption,
} = {}) {
  if (lockedAspectRatio) return lockedAspectRatio;
  if (isWallpaperCropper) return 0.85;
  if (isWheelPortrait) return 1;
  if (activeTool === "crop") return optionToAspectRatio(selectedOption);
  return NaN;
}
